import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useGetArticles } from '../../api/article';
import useCart from '../../providers/cart';
import { CheckoutStep } from '../../preload/pages/CheckoutPage/CheckoutProvider';
import { useIsAuthorized } from '../../providers/member';
import Emitter from '../../utils/events';
import useArticleStore from '../useArticleStore';

import {
  EV_ADD_TO_CART,
  EV_BEGIN_PAYMENT_FAILED,
  EV_BEGIN_PAYMENT,
  EV_BLOCK_CLICKED,
  EV_BLOCK_IMPRESSION,
  EV_CART_VIEW,
  EV_CHECKOUT_OPTION,
  EV_CHECKOUT_VIEW,
  EV_GENERIC,
  EV_PAGE_VIEW,
  EV_PAYMENT_ABORTED,
  EV_PAYMENT_BROWSER_BACK,
  EV_PRODUCT_CLICK,
  EV_PRODUCT_IMPRESSION,
  EV_PRODUCT_VIEW,
  EV_PURCHASE,
  EV_REMOVE_FROM_CART,
  EV_SEARCH_NO_HITS,
  EV_SEARCH_NO_SUGGESTIONS,
  EV_SEARCH_ON_SUGGESTION_CLICK,
  EV_UPDATE_POSTAL,
  EV_ALERTBANNER_CLICKED,
} from '../../tracking/events';
import useContext from '../useContext';
import { DeliveryStrategyContext } from '../../preload/pages/CheckoutPage/Delivery/DeliveryStrategyProvider';

const useEmitAsyncVariant = (event: string) => {
  const { getArticlesAsync } = useArticleStore();

  return useCallback(
    (variantId: string, variant?: string, quantity = 1) => {
      getArticlesAsync([variantId]).then(([product]) => {
        Emitter.emit(event, product, variant, quantity);
      });
    },
    [event],
  );
};

const mapArticlesToQuantity = (articles: IProduct[], cart?: Commerce.ICart | Commerce.IOrderCart) =>
  articles.map((a) => ({
    ...a,
    quantity: cart?.items.find((c) => c.variantId === a.id)?.quantity,
  }));

export const useEmitAddToCart = () => useEmitAsyncVariant(EV_ADD_TO_CART);
export const useEmitRemoveFromCart = () => useEmitAsyncVariant(EV_REMOVE_FROM_CART);

export const useTrackProductView = (product: IArticle) => {
  useEffect(() => {
    Emitter.emit(EV_PRODUCT_VIEW, product);
  }, [product]);
};

export const useTrackCartView = () => {
  const tracked = useRef(false);
  const { cart } = useCart();
  const variants = useMemo(() => cart?.items?.map((item) => item.variantId) || [], [cart]);
  const { articles } = useGetArticles(variants);

  const products = mapArticlesToQuantity(articles, cart);

  const enabled =
    cart && (cart.items.length === articles.length || !cart?.items.length) && !tracked.current;

  useEffect(() => {
    if (enabled) {
      tracked.current = true;
      Emitter.emit(EV_CART_VIEW, {
        products,
        value: cart?.total,
      });
    }
  }, [articles, enabled]);
};

export const useTrackCheckout = (step: CheckoutStep, options?: string) => {
  const trackedOnce = useRef(false);
  const { cart } = useCart();
  const variants = useMemo(() => cart?.items?.map((item) => item.variantId) || [], [cart]);
  const { articles } = useGetArticles(variants);
  const { deliverySelectionBuckets } = useContext(DeliveryStrategyContext);

  const enabled = cart && ((!!cart.items.length && !!articles.length) || !cart?.items.length);

  const products = mapArticlesToQuantity(articles, cart);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const selectedStrategyDeliveryValues = deliverySelectionBuckets
      .map((bucket) => bucket?.delivery?.price ?? 0)
      ?.reduce((a, b) => a + b, 0);

    const selectedDeliveryMethods = deliverySelectionBuckets
      .map((bucket) => bucket?.delivery?.method)
      .join(',');

    if (!trackedOnce.current) {
      Emitter.emit(EV_CHECKOUT_VIEW, {
        products,
        step: step + 1,
        option: options,
        selectedStrategyDeliveryValues,
      });
      trackedOnce.current = true;
    } else if (options === selectedDeliveryMethods || step !== 0) {
      Emitter.emit(EV_CHECKOUT_OPTION, {
        products,
        step: step + 1,
        option: selectedDeliveryMethods,
        selectedStrategyDeliveryValues,
      });
    }
  }, [articles, enabled, options, deliverySelectionBuckets]);
};

export const useTrackBeginPayment = () =>
  useCallback((step: number, method: string) => {
    Emitter.emit(EV_BEGIN_PAYMENT, step, method);
  }, []);

export const useTrackPaymentAborted = () =>
  useCallback((status: string) => {
    Emitter.emit(EV_PAYMENT_ABORTED, status);
  }, []);

export const useTrackBeginPaymentFailed = () =>
  useCallback((error: string) => {
    Emitter.emit(EV_BEGIN_PAYMENT_FAILED, error);
  }, []);

export const useTrackPaymentBrowserBack = () =>
  useCallback(() => {
    Emitter.emit(EV_PAYMENT_BROWSER_BACK);
  }, []);

const _trackedOrders: string[] = [];

export const useTrackPurchase = (order?: Commerce.IOrderResponse) => {
  const cartId = useRef(useCart()?.cartId);
  const member = useIsAuthorized();
  const variants = useMemo(() => order?.cart?.items.map((item) => item.variantId), [order]);
  const { articles } = useGetArticles(variants);
  const products = mapArticlesToQuantity(articles, order?.cart);

  useEffect(() => {
    const articlesFetched = !!articles.length;
    const correctCartId = !!cartId.current && !!order?.cart?.id && cartId.current === order.cart.id;
    const isOrderCart = cartId.current === order?.cart?.id;
    const notTracked = _trackedOrders.indexOf(order?.id as string) === -1;

    const selectedDeliveryMethodsAsCommaSeparatedString = order?.deliveries
      ?.map((bucket) =>
        bucket.delivery?.method === 'Store'
          ? bucket.delivery?.mioStore?.id
          : bucket.delivery?.method,
      )
      .join(',');

    if (articlesFetched && correctCartId && isOrderCart && notTracked) {
      _trackedOrders.push(order.id);

      Emitter.emit(
        EV_PURCHASE,
        products,
        order,
        selectedDeliveryMethodsAsCommaSeparatedString,
        member,
      );
    }
  }, [articles, cartId, order, member]);
};

export const useTrackSearch = (result: ICategory, query?: string) => {
  const hits =
    (result?.totalHits || 0) +
    (result?.documents?.length || 0) +
    (result?.pages?.length || 0) +
    (result?.stores?.length || 0);

  useEffect(() => {
    if (!!query && !hits) {
      Emitter.emit(EV_SEARCH_NO_HITS, query);
    }

    if (!!query && !hits && !result.didYouMean) {
      Emitter.emit(EV_SEARCH_NO_SUGGESTIONS, query);
    }
  }, [query, hits, result.didYouMean]);

  const onSuggestionClick = useCallback(() => {
    if (!!query && !!result.didYouMean) {
      Emitter.emit(EV_SEARCH_ON_SUGGESTION_CLICK, query, result.didYouMean);
    }
  }, [query, result.didYouMean]);

  return {
    onSuggestionClick,
  };
};

export const useTrackPageView = () =>
  useCallback((nextTitle?: string) => {
    Emitter.emit(EV_PAGE_VIEW, nextTitle);
  }, []);

export const useTrackGenericEvent = (event: any) => {
  return useCallback(() => {
    Emitter.emit(EV_GENERIC, event);
  }, [event]);
};

export const useFireTrackingEvent = () =>
  useCallback((event: any) => {
    Emitter.emit(EV_GENERIC, event);
  }, []);

export const useTrackProductImpression = (list: string, position: number) =>
  useCallback(
    (product: IProduct) => {
      Emitter.emit(EV_PRODUCT_IMPRESSION, list, position, product);
    },
    [list, position],
  );

export const useTrackProductClick = (list: string, position: number) =>
  useCallback(
    (product: IProduct) => {
      Emitter.emit(EV_PRODUCT_CLICK, list, position, product);
    },
    [list, position],
  );

export const useTrackUpdatePostal = () =>
  useCallback((postal: string) => {
    Emitter.emit(EV_UPDATE_POSTAL, postal);
  }, []);

export const useTrackBlockImpression = () =>
  useCallback((blockType: string, order: number) => {
    Emitter.emit(EV_BLOCK_IMPRESSION, blockType, order);
  }, []);

export const useTrackBlockClicked = () =>
  useCallback((blockType: string, order: number, href: string) => {
    Emitter.emit(EV_BLOCK_CLICKED, blockType, order, href);
  }, []);

export const useTrackAlertBanner = (alertBannerName: string) =>
  useCallback(() => {
    Emitter.emit(EV_ALERTBANNER_CLICKED, alertBannerName);
  }, []);
